import {
  BaseDecisionPriority,
  DecisionContext,
  DecisionResult,
  MoveInfo,
} from "..\/..\/interfaces\/DecisionPriority";
import { MCTSMoveGenerationService } from "..\/..\/..\/utils\/MCTSMoveGenerationService";
import {
  MCTSUtils_v2,
  cloneFullGameState,
  clonePieceWithPrototype,
} from "..\/..\/..\/utils\/MCTSUtils";

/**
 * Priority 8: Race Block Remain
 *
 * CRITICAL: Prevent moving pieces that currently block enemy victory paths
 * Uses 3-ply simulation to detect when moving a piece would allow enemy race victory
 */
export class RaceBlockRemainPriority extends BaseDecisionPriority {
  readonly priority = 8;
  readonly name = "Race Block Remain";
  readonly description = "Keep immune blockers in defensive positions";

  private analysisLogs: string[] = [];

  isRelevant(context: DecisionContext): boolean {
    return true; // Always check for critical blocking positions
  }

  analyze(context: DecisionContext): DecisionResult {
    this.analysisLogs = [];

    const gameState = context.gameState;
    const enemyIsLight = !gameState.isLightTurn;
    const enemyVictoryRow = enemyIsLight ? 7 : 0;

    // DEBUG: Log the basic setup
    this.addLog(
      `🔍 Setup: ${gameState.isLightTurn ? "Light" : "Dark"} turn, Enemy=${
        enemyIsLight ? "Light" : "Dark"
      }, Victory row=${enemyVictoryRow}`
    );

    // Find moves that would dangerously open enemy victory paths
    const dangerousMoves = this.findMovesOpeningVictoryPaths(
      context.allPossibleMoves,
      gameState,
      enemyVictoryRow
    );

    if (dangerousMoves.length === 0) {
      return {
        shouldEarlyExit: false,
        reasoning: "No dangerous blocking moves detected",
        priority: this.priority,
        diagnostics: { logs: this.analysisLogs },
      };
    }

    // Filter out dangerous moves - keep only safe moves
    const safeMoves = context.allPossibleMoves.filter(
      (move) =>
        !dangerousMoves.some((dangerous) => this.areMovesEqual(move, dangerous))
    );

    if (safeMoves.length === 0) {
      this.addLog(
        `All ${dangerousMoves.length} moves would open enemy victory paths! Taking least dangerous move.`
      );
      this.addLog(`🚨 FILTERED MOVES SUMMARY:`);
      for (let i = 0; i < Math.min(dangerousMoves.length, 5); i++) {
        const move = dangerousMoves[i];
        this.addLog(
          `   ${i + 1}. [${move.fromPosition.row},${
            move.fromPosition.col
          }] → [${move.move.position.row},${
            move.move.position.col
          }] - Opens victory path`
        );
      }
      if (dangerousMoves.length > 5) {
        this.addLog(`   ... and ${dangerousMoves.length - 5} more moves`);
      }

      // FIXED: When all moves are dangerous, early exit with first move as emergency action
      return this.createEarlyExitResult(
        dangerousMoves[0],
        "CRITICAL DEFENSE: All moves dangerous - taking emergency action",
        {
          logs: this.analysisLogs,
          filtered: dangerousMoves.length,
          safeMoves: 0,
        }
      );
    }

    // If we filtered many moves, be more decisive
    const filterRatio = dangerousMoves.length / context.allPossibleMoves.length;
    if (filterRatio >= 0.6) {
      // Take first safe move when majority of moves are dangerous
      this.addLog(
        `Critical defense: filtered ${dangerousMoves.length} dangerous moves`
      );
      return this.createEarlyExitResult(
        safeMoves[0],
        `Critical defense - majority of moves dangerous`,
        { logs: this.analysisLogs, filtered: dangerousMoves.length }
      );
    }

    this.addLog(
      `🛡️ Filtered ${dangerousMoves.length} moves that would open enemy victory paths, kept ${safeMoves.length} safe moves`
    );
    this.addLog(`🚨 FILTERED MOVES SUMMARY:`);
    for (let i = 0; i < Math.min(dangerousMoves.length, 3); i++) {
      const move = dangerousMoves[i];
      this.addLog(
        `   ❌ [${move.fromPosition.row},${move.fromPosition.col}] → [${move.move.position.row},${move.move.position.col}] - Opens victory path`
      );
    }
    if (dangerousMoves.length > 3) {
      this.addLog(
        `   ... and ${dangerousMoves.length - 3} more dangerous moves`
      );
    }

    return {
      shouldEarlyExit: false,
      filteredMoves: safeMoves,
      reasoning: `Filtered ${dangerousMoves.length} moves that would open enemy victory paths`,
      priority: this.priority,
      diagnostics: {
        logs: this.analysisLogs,
        dangerousCount: dangerousMoves.length,
      },
    };
  }

  private addLog(message: string): void {
    this.analysisLogs.push(message);
  }

  /**
   * 3-PLY SIMULATION: Find moves that would open enemy victory paths
   */
  private findMovesOpeningVictoryPaths(
    allMoves: MoveInfo[],
    gameState: any,
    enemyVictoryRow: number
  ): MoveInfo[] {
    const dangerousMoves: MoveInfo[] = [];
    const moveService = MCTSMoveGenerationService.getInstance();
    const loggedPieces = new Set<string>(); // Avoid duplicate logs

    // DEBUG: Log the analysis setup
    this.addLog(
      `🔍 Analyzing ${allMoves.length} moves for enemy victory row ${enemyVictoryRow}`
    );

    for (const moveInfo of allMoves) {
      // DEBUG: Add logging for this specific move
      const debugMove = `[${moveInfo.fromPosition.row},${moveInfo.fromPosition.col}] → [${moveInfo.move.position.row},${moveInfo.move.position.col}]`;

      // Step 1: Simulate making our move
      const testGameState = this.simulateMove(gameState, moveInfo);

      // Step 2: Switch to enemy turn and generate their moves
      const originalTurn = testGameState.isLightTurn;
      testGameState.isLightTurn = !originalTurn; // Switch to enemy turn

      try {
        const enemyMoves = moveService.getAllMoves(testGameState);

        // DEBUG: Count how many enemy KING moves can reach victory row
        let victoryMoves = 0;
        const victoryMovesDetails = [];
        for (const enemyMove of enemyMoves) {
          if (enemyMove.move.position.row === enemyVictoryRow) {
            // FIXED: Only check for KING victories - regular pieces can't win by racing!
            const enemyPiece = MCTSUtils_v2.getPieceAt(
              testGameState.board,
              enemyMove.fromPosition
            );
            if (enemyPiece && enemyPiece.isKing) {
              victoryMoves++;
              victoryMovesDetails.push(
                `[${enemyMove.fromPosition.row},${enemyMove.fromPosition.col}] → [${enemyMove.move.position.row},${enemyMove.move.position.col}] (${enemyPiece.type} king)`
              );
            }
          }
        }

        // Step 3: Check if enemy KING can reach victory row
        const canEnemyWin = enemyMoves.some((enemyMove) => {
          if (enemyMove.move.position.row === enemyVictoryRow) {
            const enemyPiece = MCTSUtils_v2.getPieceAt(
              testGameState.board,
              enemyMove.fromPosition
            );
            return enemyPiece && enemyPiece.isKing;
          }
          return false;
        });

        // DEBUG: Special logging for suspicious Crane moves (first occurrence only)
        if (
          moveInfo.fromPosition.row === 0 &&
          moveInfo.fromPosition.col === 4 &&
          dangerousMoves.length === 0
        ) {
          this.addLog(`🔍 CRANE ANALYSIS: ${debugMove}`);
          this.addLog(
            `  Victory row: ${enemyVictoryRow}, Turn: ${gameState.isLightTurn} → ${testGameState.isLightTurn}`
          );
          this.addLog(
            `  Enemy can win: ${canEnemyWin} (${victoryMoves} moves)`
          );
          if (victoryMoves > 0) {
            this.addLog(
              `  Victory moves: ${victoryMovesDetails.slice(0, 2).join(", ")}`
            );
          }
        }

        if (canEnemyWin) {
          dangerousMoves.push(moveInfo);
          // DEBUG: Log the first few dangerous moves
          if (dangerousMoves.length <= 3) {
            this.addLog(
              `⚡ Move ${debugMove} enables ${victoryMoves} enemy victory moves`
            );
          }
        }
      } finally {
        // Restore original turn
        testGameState.isLightTurn = originalTurn;
      }
    }

    this.addLog(
      `📊 Found ${dangerousMoves.length} dangerous moves out of ${allMoves.length} total`
    );
    return dangerousMoves;
  }

  /**
   * Simple move simulation - creates a copy and applies the move
   */
  private simulateMove(gameState: any, moveInfo: MoveInfo): any {
    // Create a deep copy of the game state with proper piece prototypes
    const testState = cloneFullGameState(gameState);

    // FIXED: Add bounds checking for board access
    const fromRow = moveInfo.fromPosition.row;
    const fromCol = moveInfo.fromPosition.col;
    const toRow = moveInfo.move.position.row;
    const toCol = moveInfo.move.position.col;

    if (
      !testState.board ||
      !testState.board[fromRow] ||
      !testState.board[fromRow][fromCol]
    ) {
      return testState; // Return unchanged state
    }

    if (!testState.board[toRow] || !testState.board[toRow][toCol]) {
      return testState; // Return unchanged state
    }

    // Apply the move
    const fromSquare = testState.board[fromRow][fromCol];
    const toSquare = testState.board[toRow][toCol];

    // Get the piece to move
    if (!fromSquare.Pieces || fromSquare.Pieces.length === 0) {
      return testState;
    }

    // Create a new piece instance with proper prototype
    const piece = clonePieceWithPrototype(fromSquare.Pieces[0]);

    // Move the piece
    fromSquare.Pieces = [];
    toSquare.Pieces = [piece];

    return testState;
  }

  private areMovesEqual(moveA: MoveInfo, moveB: MoveInfo): boolean {
    return (
      moveA.fromPosition.row === moveB.fromPosition.row &&
      moveA.fromPosition.col === moveB.fromPosition.col &&
      moveA.move.position.row === moveB.move.position.row &&
      moveA.move.position.col === moveB.move.position.col
    );
  }
}

